'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Capacitor } from '@capacitor/core'
import { Button } from '@/components/ui/button'
import { readConsent, setConsent, applyConsent } from '@/lib/consent/store'

/**
 * Consent prompt for visitors in prior-consent jurisdictions.
 *
 * Deliberately NOT shown to everyone. Australian visitors need no prompt (see
 * lib/consent/regions.ts), and putting a wall in front of the traffic we're
 * paying to acquire would cost real sign-ups for no legal gain — so region is
 * resolved first and the banner renders only where it is actually required.
 *
 * Accessibility (WCAG 2.1 AA):
 *   1.3.1 / 4.1.2  a labelled `region` with its own heading and description
 *   2.1.1          two ordinary buttons — fully keyboard reachable
 *   2.4.3          focus is NOT trapped and NOT stolen; the banner sits at the
 *                  end of the DOM so it doesn't displace the page's reading
 *                  order, and a keyboard user reaches it by tabbing
 *   1.4.1          accept/reject differ by label, not colour
 *
 * On rejection, and while undecided, PostHog stays opted out and Consent Mode
 * stays denied — nothing non-essential is written. Turnstile and the session
 * cookie are strictly necessary and out of scope.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    let cancelled = false

    const decided = readConsent()
    if (decided !== 'unset') {
      // Returning visitor: apply their standing choice immediately, no fetch and
      // no prompt. Synchronous, so a consenting visitor loses nothing.
      applyConsent(decided)
      return
    }

    // The native iOS/Android shell is a signed-in product surface, not a public
    // website: whoever is looking at it has an account and has accepted the
    // terms, and there is no ad tag in it to gate. A cookie banner there would
    // prompt existing customers about tracking they already agreed to, and —
    // because PostHog now starts opted out — silently kill the app's own
    // telemetry (app_foregrounded, offline sync health) until they tapped it.
    if (Capacitor.isNativePlatform()) {
      setConsent('granted')
      return
    }

    // Undecided. Ask the server whether this visitor's jurisdiction requires a
    // prompt. Failing this request must not grant consent by omission, so the
    // catch prompts rather than assumes.
    void fetch('/api/consent/region', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { consentRequired: true }))
      .then((data: { consentRequired?: boolean }) => {
        if (cancelled) return
        if (data?.consentRequired === false) {
          // No prior-consent rule here: behave as the site always has.
          setConsent('granted')
          return
        }
        setVisible(true)
      })
      .catch(() => {
        if (!cancelled) setVisible(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Move focus to the banner's heading when it appears. Announced without
  // hijacking the page: the heading is focusable only programmatically
  // (tabIndex -1), so it never becomes a stray tab stop afterwards.
  useEffect(() => {
    if (visible) headingRef.current?.focus()
  }, [visible])

  if (!visible) return null

  const decide = (state: 'granted' | 'denied') => {
    setConsent(state)
    setVisible(false)
  }

  return (
    <div
      role='region'
      aria-labelledby='cookie-banner-heading'
      aria-describedby='cookie-banner-description'
      className='fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background/95 p-4 backdrop-blur-sm sm:p-6'
    >
      <div className='mx-auto flex max-w-4xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='space-y-1'>
          <h2
            id='cookie-banner-heading'
            ref={headingRef}
            tabIndex={-1}
            className='text-sm font-semibold outline-none'
          >
            Cookies and analytics
          </h2>
          <p id='cookie-banner-description' className='text-sm text-muted-foreground'>
            We use analytics to understand how people find and use this site. We only
            set these cookies if you accept.{' '}
            <Link href='/privacy' className='underline underline-offset-2'>
              Read our privacy policy
            </Link>
            .
          </p>
        </div>

        {/*
          Reject is given the same weight as Accept — same size, same row, no
          styling that buries it. The ICO is explicit that rejecting must be as
          easy as accepting; a greyed-out or hidden "reject" is the single most
          commonly enforced-against dark pattern in cookie banners.
        */}
        <div className='flex shrink-0 gap-2'>
          <Button variant='outline' onClick={() => decide('denied')} className='flex-1 sm:flex-none'>
            Reject
          </Button>
          <Button onClick={() => decide('granted')} className='flex-1 sm:flex-none'>
            Accept
          </Button>
        </div>
      </div>
    </div>
  )
}
